import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getProductTypes, DEFAULT_PRODUCT_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProductTypesContext = createContext(null);

/**
 * Provides the user-manageable product type list to the whole app.
 * Falls back to DEFAULT_PRODUCT_TYPES until the API responds (so dropdowns are never empty).
 * Reloads on every navigation (while authenticated) so changes made in Company Settings
 * — including activate/deactivate — are reflected everywhere.
 */
export function ProductTypesProvider({ children }) {
  // `all` keeps the full objects (with active flag); `productTypes` is the active names list.
  const [all, setAll] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const reload = useCallback(async () => {
    // Avoid firing (and triggering a 401 redirect) before the user is authenticated.
    if (!user) return;
    try {
      const list = await getProductTypes();
      setAll(Array.isArray(list) ? list : []);
      setLoaded(true);
    } catch (e) {
      // Transient error — keep current list / fallback defaults.
      console.error("productTypes: reload failed", e);
    }
  }, [user]);

  // Reload on mount and whenever the route changes (picks up changes after login / settings edits).
  useEffect(() => {
    reload();
  }, [reload, location.pathname]);

  const productTypes = useMemo(() => {
    const names = all.filter((p) => p.active !== false).map((p) => p.name);
    // Only fall back to defaults before the list has loaded; once loaded, respect the real list.
    if (names.length) return names;
    return loaded ? [] : DEFAULT_PRODUCT_TYPES;
  }, [all, loaded]);

  const value = useMemo(
    () => ({ productTypes, all, loaded, reload }),
    [productTypes, all, loaded, reload],
  );

  return <ProductTypesContext.Provider value={value}>{children}</ProductTypesContext.Provider>;
}

export function useProductTypes() {
  const ctx = useContext(ProductTypesContext);
  if (!ctx) {
    return { productTypes: DEFAULT_PRODUCT_TYPES, all: [], loaded: false, reload: () => {} };
  }
  return ctx;
}
